import React, { useState, useEffect } from "react";
import { Form, Select, Typography, InputNumber, Row, Col, Checkbox } from "antd";

import axios from "axios";
import { useTranslation } from "react-i18next";
import { elvis } from "../../Utils"

const { Option, OptGroup } = Select;
const { Title } = Typography;

const BuildInfoForm = props => {
    const { t } = useTranslation();
    const [qualityTypes, setQualityTypes] = useState([]);
    const [conservationTypes, setConservationTypes] = useState([]);
    const [retrofittingTypes, setRetrofittingTypes] = useState([]);
    const [regularityTypes, setRegularityTypes] = useState([]);
    const [materialTypes, setMaterialTypes] = useState([]);
    const [positionTypes, setPositionTypes] = useState([]);
    const [groundFloorTypes, setGroundFloorTypes] = useState([]);
    const [form] = Form.useForm();
    
    const isEditMode = props.isEditMode || false;

    const formItemLayout = {
        labelCol: {
            sm: { span: 8 }
        },
        wrapperCol: {
            sm: { span: 16 }
        }
    };

    useEffect(() => {
        axios
            .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/qualities")
            .then(({ data }) => {
                setQualityTypes(data);
            })
            .catch(function (error) {
                console.log(error);
            });

        axios
            .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/conservations")
            .then(({ data }) => {
                setConservationTypes(data);
            })
            .catch(function (error) {
                console.log(error);
            });

        axios
            .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/retrofittings")
            .then(({ data }) => {
                setRetrofittingTypes(data);
            })
            .catch(function (error) {
                console.log(error);
            });

        axios
            .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/regularities")
            .then(({ data }) => {
                setRegularityTypes(data);
            })
            .catch(function (error) {
                console.log(error);
            });

        axios
            .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/materials")
            .then(({ data }) => {
                setMaterialTypes(data);
            })
            .catch(function (error) {
                console.log(error);
            });

        axios
            .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/positions")
            .then(({ data }) => {
                setPositionTypes(data);
            })
            .catch(function (error) {
                console.log(error);
            });

        axios
            .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/ground-floors")
            .then(({ data }) => {
                setGroundFloorTypes(data);
            })
            .catch(function (error) {
                console.log(error);
            });

        if (props.record.enterprise) {
            form.setFieldsValue({
                year: props.record.enterprise.year,
                quality: props.record.enterprise.quality,
                conservation: props.record.enterprise.conservation,
                retrofitting: props.record.enterprise.retrofitting,
                inhabitants: props.record.enterprise.inhabitants,
                constructionCost: props.record.enterprise.constructionCost,
                economicValue: props.record.enterprise.economicValue,
                regularity: props.record.enterprise.regularity,
                verticalMat: props.record.enterprise.verticalMat,
                horizontalMat: props.record.enterprise.horizontalMat,
                roofMat: props.record.enterprise.roofMat,
                buildPosition: props.record.enterprise.buildPosition,
                groundFloor: props.record.enterprise.groundFloor,
                nFloors: props.record.enterprise.nFloors,
                floorHeight: props.record.enterprise.floorHeight,
                nUnderFloors: props.record.enterprise.nUnderFloors,
                floorArea: props.record.enterprise.floorArea,
                culturalHeritage: props.record.enterprise.culturalHeritage || false,
                heritageTier: props.record.enterprise.heritageTier,
            });
        }

    }, [props.record.enterprise, form]);

    // BuildingMaterial is a flat enum; the two material families are rebuilt here as OptGroups
    // from the value-name prefix. Values matching no prefix render as standalone options.
    const materialFamilies = [
        { label: "Masonry", prefix: "MASONRY_" },
        { label: "Reinforced concrete", prefix: "RC_" },
    ];

    const createMaterialSelect = () => {
        const renderOption = name => (
            <Option key={name} value={name}>
                {t('buildingEnums.material.' + name, name)}
            </Option>
        );
        const grouped = new Set();
        const groups = materialFamilies.map(family => {
            const options = materialTypes.filter(name => name.startsWith(family.prefix));
            options.forEach(name => grouped.add(name));
            return { label: family.label, options };
        });
        const standalone = materialTypes.filter(name => !grouped.has(name));
        return [
            ...groups
                .filter(group => group.options.length > 0)
                .map(group => (
                    <OptGroup key={group.label} label={group.label}>
                        {group.options.map(renderOption)}
                    </OptGroup>
                )),
            ...standalone.map(renderOption),
        ];
    }

    const onValuesChange = (changedValues) => {
        form.validateFields(Object.keys(changedValues));
    };

    const onFieldsChange = (changedFields, allFields) => {
        // Don't process changes if not in edit mode
        if (!isEditMode) return;
        
        const values = form.getFieldsValue();
        var valid = false;

        if (form.getFieldsError().every(e => e === undefined || e.errors.length === 0)) {
            valid = true;
        }

        const newData = { ...props.record };
        newData.enterprise.year = elvis(values.year);
        newData.enterprise.quality = elvis(values.quality);
        newData.enterprise.conservation = elvis(values.conservation);
        newData.enterprise.retrofitting = elvis(values.retrofitting);
        newData.enterprise.inhabitants = elvis(values.inhabitants);
        newData.enterprise.constructionCost = elvis(values.constructionCost);
        newData.enterprise.economicValue = elvis(values.economicValue);
        newData.enterprise.regularity = elvis(values.regularity);
        newData.enterprise.verticalMat = elvis(values.verticalMat);
        newData.enterprise.horizontalMat = elvis(values.horizontalMat);
        newData.enterprise.roofMat = elvis(values.roofMat);
        newData.enterprise.buildPosition = elvis(values.buildPosition);
        newData.enterprise.groundFloor = elvis(values.groundFloor);
        newData.enterprise.nFloors = elvis(values.nFloors);
        newData.enterprise.floorHeight = elvis(values.floorHeight);
        newData.enterprise.nUnderFloors = elvis(values.nUnderFloors);
        newData.enterprise.floorArea = elvis(values.floorArea);
        newData.enterprise.culturalHeritage = values.culturalHeritage || false;
        newData.enterprise.heritageTier = values.culturalHeritage ? elvis(values.heritageTier) : null;
        newData.valid = valid;
        props.parentCallback(newData);
    };

    return (
        <Form
            form={form}
            {...formItemLayout}
            className="details_form"
            onValuesChange={onValuesChange}
            onFieldsChange={onFieldsChange}
        >
            <Row>
                <Col span={11}>
                    <Title level={4}>Construction details</Title>
                    <Form.Item label="Construction year" name="year">
                        <InputNumber disabled={!isEditMode} />
                    </Form.Item>

                    <Form.Item label="Quality" name="quality">
                        <Select disabled={!isEditMode}>
                            {qualityTypes.map(name => (
                                <Option key={name} value={name}>
                                    {t('buildingEnums.quality.' + name, name)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Conservation" name="conservation">
                        <Select disabled={!isEditMode}>
                            {conservationTypes.map(name => (
                                <Option key={name} value={name}>
                                    {t('buildingEnums.conservation.' + name, name)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Retrofitting" name="retrofitting">
                        <Select disabled={!isEditMode}>
                            {retrofittingTypes.map(name => (
                                <Option key={name} value={name}>
                                    {t('buildingEnums.retrofitting.' + name, name)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Regularity" name="regularity">
                        <Select disabled={!isEditMode}>
                            {regularityTypes.map(name => (
                                <Option key={name} value={name}>
                                    {t('buildingEnums.regularity.' + name, name)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Title level={4}>Others</Title>
                    <Form.Item label="N. of inhabitants" name="inhabitants">
                        <InputNumber disabled={!isEditMode} />
                    </Form.Item>
                    <Form.Item label="Construction cost (€/sm)" name="constructionCost">
                        <InputNumber disabled={!isEditMode} />
                    </Form.Item>
                    <Form.Item label="Economic value (€)" name="economicValue">
                        <InputNumber disabled={!isEditMode} />
                    </Form.Item>

                    <Title level={4}>Cultural heritage</Title>
                    <Form.Item
                        label="Cultural heritage"
                        name="culturalHeritage"
                        valuePropName="checked"
                    >
                        <Checkbox disabled={!isEditMode} />
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.culturalHeritage !== curr.culturalHeritage}
                    >
                        {({ getFieldValue }) => getFieldValue('culturalHeritage') ? (
                            <Form.Item label="Heritage tier" name="heritageTier">
                                <Select disabled={!isEditMode} allowClear>
                                    <Option value="WORLD_HERITAGE">World Heritage</Option>
                                    <Option value="HAGUE_CONVENTION">Hague Convention</Option>
                                    <Option value="NATIONAL">National</Option>
                                    <Option value="SUB_NATIONAL">Sub-national</Option>
                                    <Option value="LOCAL">Local</Option>
                                </Select>
                            </Form.Item>
                        ) : null}
                    </Form.Item>
                </Col>
                <Col span={2} />
                <Col span={11}>
                    <Title level={4}>Typology</Title>
                    <Form.Item label="Vertical structure material" name="verticalMat">
                        <Select disabled={!isEditMode}>
                            {createMaterialSelect()}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Horizontal structure material" name="horizontalMat">
                        <Select disabled={!isEditMode}>
                            {createMaterialSelect()}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Roof material" name="roofMat">
                        <Select disabled={!isEditMode}>
                            {createMaterialSelect()}
                        </Select>
                    </Form.Item>
                    <Title level={4}>Geometry</Title>
                    <Form.Item label="Position" name="buildPosition">
                        <Select disabled={!isEditMode}>
                            {positionTypes.map(name => (
                                <Option key={name} value={name}>
                                    {t('buildingEnums.position.' + name, name)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Type of ground floor" name="groundFloor">
                        <Select disabled={!isEditMode}>
                            {groundFloorTypes.map(name => (
                                <Option key={name} value={name}>
                                    {t('buildingEnums.groundFloor.' + name, name)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="N. of floors" name="nFloors">
                        <InputNumber disabled={!isEditMode} />
                    </Form.Item>
                    <Form.Item label="Average floor height (cm)" name="floorHeight">
                        <InputNumber disabled={!isEditMode} />
                    </Form.Item>
                    <Form.Item label="N. of underground floor" name="nUnderFloors">
                        <InputNumber disabled={!isEditMode} />
                    </Form.Item>
                    <Form.Item label="Average floor area (sm)" name="floorArea">
                        <InputNumber disabled={!isEditMode} />
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};

export default BuildInfoForm;